import React, { createContext, useState, useContext, useEffect } from 'react';
import { 
  login as loginApi, 
  logout as logoutApi, 
  isAuthenticated, 
  getUserData, 
  validateToken,
  checkApiConnection
} from '../services/auth';

// Создаем контекст авторизации
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiStatus, setApiStatus] = useState({
    connected: false,
    checking: true
  });

  // Проверяем подключение к API и авторизацию при загрузке приложения
  useEffect(() => {
    const init = async () => {
      try {
        // Проверяем соединение с API
        const apiCheck = await checkApiConnection();
        setApiStatus({
          connected: apiCheck.connected,
          checking: false,
          serverInfo: apiCheck.data
        });

        // Проверяем авторизацию
        if (isAuthenticated()) {
          // Проверяем токен
          if (apiCheck.connected) {
            try {
              const tokenStatus = await validateToken();
              if (!tokenStatus.valid) {
                // Если токен недействителен, выходим
                logout();
                return;
              }
            } catch (tokenErr) {
              console.warn('Token validation failed:', tokenErr);
            }
          }
          
          // Получаем данные пользователя
          const userData = getUserData() || { isAdmin: true };
          setUser(userData);
        }
      } catch (err) {
        console.error('Initialization error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  // Функция для авторизации
  const login = async (username, password) => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await loginApi(username, password);
      
      // Получаем данные пользователя из ответа или локального хранилища
      const userData = data.user || getUserData() || { isAdmin: true };
      setUser(userData);
      
      return data;
    } catch (err) {
      // Обработка различных типов ошибок
      let errorMessage = 'Ошибка авторизации';
      
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message.includes('Network Error')) {
        errorMessage = 'Нет соединения с сервером';
        
        // Проверяем, был ли создан debug режим
        if (getUserData()) {
          const userData = getUserData();
          setUser(userData);
          return; // Не выбрасываем ошибку, если успешно вошли в режим отладки
        }
      }
      
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Функция для выхода из системы
  const logout = () => {
    logoutApi();
    setUser(null);
  };

  // Функция перепроверки соединения с API
  const checkConnection = async () => {
    setApiStatus(prev => ({ ...prev, checking: true }));
    try {
      const result = await checkApiConnection();
      setApiStatus({
        connected: result.connected,
        checking: false,
        serverInfo: result.data
      });
      return result;
    } catch (err) {
      setApiStatus({
        connected: false,
        checking: false,
        error: err.message
      });
      return { connected: false, error: err.message };
    }
  };

  // Значение контекста
  const value = {
    user,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!user,
    apiStatus,
    checkConnection
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Хук для использования контекста авторизации
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth должен использоваться внутри AuthProvider');
  }
  return context;
};
